import React, { useState } from 'react';
import MultiSelectDropdown from './MultiSelectDropdown';
import StoryModal from './StoryModal';
import successStoriesData from '../../data/success-stories-db.json';
import useResizer from '@components/extra/useResizer';

const filters = {
  transition: [
    'Fresher Transitions',
    'Non-Circuit Branch to IT Jobs',
    'Non-IT to IT (Experienced Profiles)',
    'Service to Product',
    'Testing to Development',
    'Manual to Automation',
    'Career Gap',
    'DA to DA',
    'Developer to Developer',
    'Developer to QA',
    'Fresher to DA',
    'Non IT to Developer',
    'Non IT to QA',
    'Other Domain to DA',
    'QA to QA',
    'Student to Developer',
    'Student to QA',
    'Support to Developer',
    'Support to QA'
  ],
  experience: [
    'Fresher',
    'Between 1-3 Years',
    'Between 3-5 Years',
    'More than 5 Years',
  ],
  program: [
    'Full-Stack Development',
    'Backend Development',
    'QA Automation',
    'Data Analytics',
  ],
  package: ['More than 5 LPA', 'More than 10 LPA', 'More than 15 LPA'],
};

const SuccessStories = () => {
  const allStories = successStoriesData.strapi_success_stories
    .map((story) => {
      const {
        Name,
        Transition,
        Salary,
        Program,
        Experience,
        Company,
        Role,
        HikePercentage,
        TestimonialLink,
        company_logo,
        profile_picture,
      } = story.attributes || {};

      return {
        name: Name,
        transition: Transition,
        package: Salary,
        program: Program,
        experience: Experience,
        company: Company,
        position: Role,
        hike: HikePercentage,
        videoUrl: TestimonialLink,
        companyLogoUrl: company_logo,
        imageUrl: profile_picture,
      };
    })
    .sort((a, b) => {
      const aHasVideo = !!a.videoUrl;
      const bHasVideo = !!b.videoUrl;

      if (aHasVideo && !bHasVideo) return -1;
      if (!aHasVideo && bHasVideo) return 1;
      return 0; // maintain original order if both are same
    });

  const isMobile = useResizer();

  const [selectedStory, setSelectedStory] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFilters, setSelectedFilters] = useState({
    transition: [],
    experience: [],
    program: [],
    package: [],
  });

  const pageSize = isMobile ? 3 : 6;

  const updateFilter = (key, values) => {
    setSelectedFilters((prev) => ({ ...prev, [key]: values }));
    setCurrentPage(1);
  };

  const filteredStories = allStories.filter((story) => {
    return (
      (!selectedFilters.transition.length ||
        selectedFilters.transition.includes(story.transition)) &&
      (!selectedFilters.experience.length ||
        selectedFilters.experience.includes(story.experience)) &&
      (!selectedFilters.program.length ||
        selectedFilters.program.includes(story.program)) &&
      (!selectedFilters.package.length ||
        selectedFilters.package.includes(story.package))
    );
  });

  const totalPages = Math.ceil(filteredStories.length / pageSize);
  const paginatedStories = filteredStories.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  return (
    <div className="my-10 flex flex-col items-center gap-8 bg-v5-neutral-200 py-10 px-4">
      <h2 className="font-manrope text-2xl font-extrabold md:text-[44px] md:leading-[66px]">
        Success Stories
      </h2>

      <div className="flex flex-wrap items-center justify-center gap-4">
        <h3 className="font-manrope text-[14px] font-bold text-v5-neutral-400 md:text-[20px]">
          Filter by:
        </h3>

        <MultiSelectDropdown
          label="Transition"
          options={filters.transition}
          selectedValues={selectedFilters.transition}
          onChange={(values) => updateFilter('transition', values)}
        />
        <MultiSelectDropdown
          label="Experience Level"
          options={filters.experience}
          selectedValues={selectedFilters.experience}
          onChange={(values) => updateFilter('experience', values)}
        />
        <MultiSelectDropdown
          label="Programs Chosen"
          options={filters.program}
          selectedValues={selectedFilters.program}
          onChange={(values) => updateFilter('program', values)}
        />
        <MultiSelectDropdown
          label="Package"
          options={filters.package}
          selectedValues={selectedFilters.package}
          onChange={(values) => updateFilter('package', values)}
        />
      </div>

      {paginatedStories.length > 0 ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {paginatedStories.map((story, index) => (
            <div
              key={index}
              className="max-w-[380px] overflow-hidden rounded-[12px] bg-white shadow-md md:min-h-[330px] md:w-[380px]"
            >
              <div className="relative h-[160px] w-full bg-slate-600">
                {story.hike > 25 && (
                  <div className="absolute right-0 flex items-center gap-1 rounded-bl-[12px] rounded-tr-[12px] bg-v5-green-300 px-[12px] py-1 text-center font-rubik text-[12px] font-semibold text-white shadow-md">
                    <div className="h-[14px] w-[14px]">
                      <img
                        src={`/images/HikeIcon.png`}
                        alt={story.name}
                        className="h-full w-full object-contain "
                      />
                    </div>
                    {story.hike}% salary hike after Crio
                  </div>
                )}
                <img
                  src={
                    story.imageUrl
                      ? `/images/strapi/success-stories/${story.imageUrl}`
                      : `/images/ProfilePlaceholder.png`
                  }
                  alt={story.name}
                  className="h-full w-full object-cover"
                />
                {story.companyLogoUrl && (
                  <div className="absolute left-1/2 -mt-[20px] h-[40px] w-[140px] -translate-x-1/2 rounded-[12px] border-[1px] border-v5-neutral-300 bg-white">
                    <img
                      src={`/images/strapi/success-stories/${story.companyLogoUrl}`}
                      alt={story.name}
                      className="h-full w-full object-contain "
                    />
                  </div>
                )}
              </div>
              <div className="flex flex-col items-center py-3 px-2 pt-[28px] text-center">
                <p className="text-[16px] font-semibold text-gray-700">
                  {story.name}
                </p>
                <p className="text-[12px] text-gray-400">
                  {story.position} at{' '}
                  <span className="font-semibold">{story.company}</span>
                </p>
                <p className="mt-4 text-[10px] text-gray-400">Enrolled in</p>
                <p className="text-[12px] font-semibold text-gray-600">
                  {story.program}
                </p>
              </div>
              {story.videoUrl && (
                <div
                  className="flex h-[40px] w-full cursor-pointer items-center justify-center gap-1 border-t"
                  onClick={() => setSelectedStory(story)}
                >
                  <div className="h-[14px] w-[14px]">
                    <img
                      src={`/images/PlayIcon.png`}
                      alt={story.name}
                      className="h-full w-full object-contain "
                    />
                  </div>
                  <p className="text-[12px] text-gray-700">View Story</p>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="flex items-center justify-center py-10 text-center text-sm text-gray-500">
          No success stories found.
        </div>
      )}

      {/* Pagination */}
      <div className="mt-4 flex items-center gap-2 font-manrope font-bold md:text-[16px]">
        {currentPage > 3 && (
          <>
            <button
              onClick={() => setCurrentPage(1)}
              className={`flex h-8 w-8 items-center justify-center rounded-full ${
                currentPage === 1
                  ? 'bg-v5-green-300 text-white'
                  : 'text-v5-neutral-500'
              }`}
            >
              1
            </button>
            <span className="text-v5-neutral-500">...</span>
          </>
        )}

        {Array.from({ length: totalPages }).map((_, i) => {
          const pageNumber = i + 1;
          const isInRange =
            pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2;

          return isInRange ? (
            <button
              key={i}
              onClick={() => setCurrentPage(pageNumber)}
              className={`flex h-8 w-8 items-center justify-center rounded-full ${
                currentPage === pageNumber
                  ? 'bg-v5-green-300 text-white'
                  : 'text-v5-neutral-500'
              }`}
            >
              {pageNumber}
            </button>
          ) : null;
        })}

        {currentPage < totalPages - 2 && (
          <>
            <span className="text-v5-neutral-500">...</span>
            <button
              onClick={() => setCurrentPage(totalPages)}
              className={`flex h-8 w-8 items-center justify-center rounded-full ${
                currentPage === totalPages
                  ? 'bg-v5-green-300 text-white'
                  : 'text-v5-neutral-500'
              }`}
            >
              {totalPages}
            </button>
          </>
        )}
      </div>

      <StoryModal
        story={selectedStory}
        onClose={() => setSelectedStory(null)}
      />
    </div>
  );
};

export default SuccessStories;
